import { MediaImage, useMediaDisplayUrl } from '@/lib/mediaDisplay';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiApi,
  aiModelsApi,
  promptTemplatesApi,
  ApiError,
  mediaApi,
  pagesApi,
  postsApi,
  themesApi,
  sitesApi,
  siteKeys,
  type AiChatContext,
  type AiPinnedContext,
  type AiChatRequest,
  type AiModel,
  type AiSelectionContext,
  type AiToolCall,
  type MediaFolder,
  type PageEditorMode,
} from '@wordpuppi/shared';
import {
  ArrowUp,
  AtSign,
  Check,
  ChevronRight,
  Clock,
  ExternalLink,
  Paperclip,
  Loader2,
  PanelBottom,
  PanelRight,
  Plus,
  RefreshCw,
  Settings,
  Sparkles,
  Wand2,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UpgradeNotice } from '@/components/UpgradeNotice';
import { confirmDialog } from '@/lib/dialog';
import { useEditorContext } from '@/lib/editorContext';
import { pickFilesDesktop } from '@/utils/desktopActions';
import { cn } from '@/lib/utils';
import { useBaxterEffects } from '@/lib/baxterEffects';
import { BaxterMark } from './BaxterMark';
import { renderMarkdown } from './markdown';
import { applyToolEffects, notifyMediaChange } from '@/lib/mediaEffects';
import { previewLinkFor, barkLinksFor } from './toolLinks';
import { touchedPage } from './toolEffects';
import { Thinking } from './WorkingStatus';
import {
  AiSettingsDialog,
  FREE_EXHAUSTED_COPY,
  OPENROUTER_KEYS_URL,
} from './AiSettingsDialog';
import { BAXTER_BRIEF_EVENT, readBaxterBrief, type BaxterBrief, type BaxterRevertPage } from './baxterBrief';
import {
  acceptAttachments,
  ATTACH_ACCEPT,
  attachmentMime,
  Attachments,
  FileChip,
  isImage,
  AUTO_ID,
  BAXTER_FOLDER,
  canSeeImages,
  imagesFromClipboard,
  NO_VISION_NOTE,
  withAttachmentNote,
} from './attachments';
import { TemplateGallery } from './TemplateGallery';
import { ModelPicker } from './ModelPicker';
import { ModelMeter } from './ModelMeter';

/** Server default (contract: platform key is free-gated to this unless BYOK). */
// One round-trippable value: `provider:model`, the same shape the backend
// stores and returns, so a restored chat's `model` drops straight into the
// picker. AB#669.
const DEFAULT_MODEL = `openrouter:${AUTO_ID}`;

const MODEL_STORAGE_KEY = 'wpp-baxter-model';
/** Stable identity while the catalog loads — `?? []` re-rendered the picker on every keystroke. AB#837. */
const NO_MODELS: AiModel[] = [];
// Per SITE: chats live in the per-site content DB, so site A's id 404s on B.
const chatStorageKey = (siteSlug: string) => `wpp-baxter-chat:${siteSlug}`;

/** Remember which chat is open (null = none) — closing the panel unmounts it. AB#645. */
function rememberChat(siteSlug: string, id: string | null) {
  try {
    if (id) localStorage.setItem(chatStorageKey(siteSlug), id);
    else localStorage.removeItem(chatStorageKey(siteSlug));
  } catch {
    // storage disabled — the chat just doesn't survive closing the panel
  }
}

function storedModel(): string {
  try {
    return localStorage.getItem(MODEL_STORAGE_KEY) || DEFAULT_MODEL;
  } catch {
    return DEFAULT_MODEL; // private mode / storage disabled
  }
}

/** Catalog lookup for a served id: OpenRouter reports free models with a `:free` suffix the catalog may omit (or the other way round). */
const findServed = (models: AiModel[], served: string) =>
  models.find((m) => m.id.replace(/:free$/, '') === served.replace(/:free$/, ''));

/** A message in panel state — same shape as the contract's UiMessage, mutable. */
interface Msg {
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: AiToolCall[];
  /** Object URLs for pictures sent with this turn (this mount only). */
  images?: string[];
  /** AB#949: non-picture files sent with this turn, shown as chips. */
  files?: { name: string; size: number; mime: string }[];
  /** AB#810: the canvas selection this turn carried (user turns). */
  selection?: AiSelectionContext;
  pinned_context?: AiPinnedContext[];
  /** AB#810: the page bytes Revert puts back, for the assistant turn that follows. */
  revert?: BaxterRevertPage;
}

interface Pending {
  id: string;
  name: string;
  args: unknown;
  reason?: string;
}

/** `wpp_update_content` -> "Update content" */
const humanizeTool = (name: string) => {
  const words = name.replace(/^wpp_/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * renderMarkdown re-parses the WHOLE reply on every `delta`, so without this a
 * long answer re-rendered every earlier message's subtree per token. Memoized
 * on `content`, only the message actually growing does the work. AB#645.
 */
const AssistantText = memo(function AssistantText({ content }: { content: string }) {
  return (
    <div
      className="baxter-assistant-output break-words [&>*:first-child]:mt-0"
      // Sanitized in renderMarkdown (escape-first, http(s) links only).
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
});

/** `memo`: a chat with a dozen tool calls re-serialised every one of them per keystroke. AB#837. */
const ToolRow = memo(function ToolRow({ call, siteSlug }: { call: AiToolCall; siteSlug: string }) {
  const screenshotSrc = useMediaDisplayUrl(call.screenshot_url ?? '', siteSlug);
  const [open, setOpen] = useState(false);
  const previewUrl = previewLinkFor(call);
  const barkLinks = barkLinksFor(call, siteSlug);
  // `args` can be a whole page of HTML, and this is only ever read when expanded.
  const detail =
    call.summary ?? (open && call.args ? JSON.stringify(call.args, null, 2) : '');
  return (
    <div className="rounded-md border bg-muted/30 px-2 py-1.5 text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left outline-none rounded-sm focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <ChevronRight className={cn('size-3 shrink-0 transition-transform ease-out', open && 'rotate-90')} />
        <span className="flex-1 truncate font-medium">{humanizeTool(call.name)}</span>
        {call.status === 'pending' && (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" aria-label="Running" />
        )}
        {call.status === 'ok' && <Check className="size-3.5 shrink-0 text-success" aria-label="Done" />}
        {call.status === 'error' && <X className="size-3.5 shrink-0 text-destructive" aria-label="Failed" />}
        {call.status === 'rejected' && <span className="shrink-0 text-muted-foreground">Skipped</span>}
      </button>
      {previewUrl && (
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1 underline"
        >
          <ExternalLink className="size-3" />
          Open preview
        </a>
      )}
      {barkLinks && <div className="mt-1.5 flex flex-wrap gap-3">
        <a href={barkLinks.calendar} className="underline">Open calendar</a>
        {barkLinks.content && <a href={barkLinks.content.path} className="underline">Open {barkLinks.content.kind === 'posts' ? 'post' : 'page'}</a>}
      </div>}
      {call.screenshot_url && (
        <a
          href={screenshotSrc}
          target="_blank"
          rel="noreferrer"
          className="mt-1.5 block overflow-hidden rounded border"
          title="Open the screenshot"
        >
          <img
            src={screenshotSrc}
            alt="Screenshot of the rendered page"
            className="max-h-40 w-full object-cover object-top"
            loading="lazy"
          />
        </a>
      )}
      {open && detail && (
        <pre className="disclosure-reveal mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
          {detail}
        </pre>
      )}
    </div>
  );
});

/**
 * The user bubble, memoized: the selection card joins every selected element's
 * HTML, which a keystroke has no reason to redo. AB#837.
 */
const UserMessage = memo(function UserMessage({ m, siteSlug }: { m: Msg; siteSlug: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] space-y-1.5 rounded-lg bg-primary/10 px-3 py-2">
        {m.images && (
          <div className="flex flex-wrap gap-1.5">
            {m.images.map((src) => (
              <MediaImage key={src} src={src} scopeKey={siteSlug} alt="" className="size-16 rounded object-cover" />
            ))}
          </div>
        )}
        {m.files && (
          <ul className="flex flex-wrap gap-1.5 text-xs" aria-label="Attached files">
            {m.files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="rounded-md border bg-background/60 px-2 py-1">
                <FileChip {...f} />
              </li>
            ))}
          </ul>
        )}
        <div className="whitespace-pre-wrap break-words">{m.content}</div>
        {m.selection && (
          // Collapsed by default: the markup is for checking, not reading.
          <details data-testid="baxter-selection-card" className="rounded-md border bg-background/60 px-2 py-1 text-xs">
            <summary className="cursor-pointer">
              Selection · {m.selection.elements.length} element{m.selection.elements.length === 1 ? '' : 's'} · {m.selection.breakpoint}
            </summary>
            <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
              {m.selection.elements.map((element) => `${element.selector}\n${element.html}`).join('\n\n')}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
});

export type BaxterDock = 'right' | 'bottom';

export function BaxterPanel({
  siteSlug,
  onClose,
  dock,
  onToggleDock,
  onCollapse,
  railAction,
}: {
  siteSlug: string;
  onClose: () => void;
  /** Which edge the panel is docked to on md+ (AB#645). */
  dock: BaxterDock;
  onToggleDock: () => void;
  onCollapse?: () => void;
  railAction?: { kind: 'new' | 'history'; siteSlug: string };
}) {
  const location = useLocation();
  const params = useParams({ strict: false }) as { postId?: string; pageId?: string };
  const search = useSearch({ strict: false }) as { bp?: string };
  const themeFile = useEditorContext((s) => s.themeFile);
  const canvasSelection = useEditorContext((s) => s.canvasSelection);
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<Msg[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [pinnedContext, setPinnedContext] = useState<AiPinnedContext[]>([]);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuIndex, setContextMenuIndex] = useState(0);
  const [selectionText, setSelectionText] = useState('');
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const [streaming, setStreaming] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastRequestRef = useRef<AiChatRequest | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [improvementsOpen, setImprovementsOpen] = useState(false);
  // What the provider ACTUALLY ran last turn. `openrouter/free` is a router, so
  // the picker's own label never names the model that answered. AB#669.
  const [served, setServed] = useState<string | null>(null);
  // Persists per chat (sent on every turn) and as the user's default for the
  // next new chat (localStorage) — AB#669.
  const [model, setModel] = useState<string>(storedModel);
  // AB#642: the site-generator skill this chat was started with (wizard brief or
  // a restored chat), plus the media the wizard uploaded for the first turn.
  const [skill, setSkill] = useState<string | null>(null);
  const [uploadedMedia, setUploadedMedia] = useState<string[]>([]);
  // AB#669: the free budget on WordPuppi's key ran out mid-stream. Not `error` —
  // the stream is still live and the model still answers.
  const [freeExhausted, setFreeExhausted] = useState(false);
  const nudgedRef = useRef(false);
  // Files (pictures, text, PDF, audio, video) waiting to ride along with the next message (uploaded on send).
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  // AB#810: page id being reverted, so only that turn's button shows the wait.
  const [reverting, setReverting] = useState<string | null>(null);
  const [visionNote, setVisionNote] = useState(false);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attachInput = useRef<HTMLInputElement>(null);
  // Bubble thumbnails outlive the chips, so they get their own URLs; revoked
  // together on unmount. ponytail: a per-message revoke would need message
  // lifecycle tracking for a handful of blobs that die with the panel anyway.
  const objectUrls = useRef<string[]>([]);
  // `tool_result` carries only the call id, so the name/args needed to work out
  // what a finished call touched have to be remembered from `tool_start`.
  const toolCalls = useRef(new Map<string, { name: string; args: unknown }>());
  const startTurn = useBaxterEffects((s) => s.startTurn);
  const endTurn = useBaxterEffects((s) => s.endTurn);

  const modelsQuery = useQuery({
    queryKey: ['sites', siteSlug, 'ai-models'],
    queryFn: () => aiModelsApi.list(siteSlug),
  });
  const models = modelsQuery.data ?? NO_MODELS;
  // Can the picked model be sent pictures at all? Gates paste and the + button.
  const modelAvailable = model === DEFAULT_MODEL || models.some((m) => m.tools && `${m.provider}:${m.id}` === model);
  const canSee = canSeeImages(models, model);
  const servedModel = served ? findServed(models, served) : undefined;
  // Only BYOK catalogs carry paid models, and that's the only question asked.
  const hasPaidModels = models.some((m) => !m.free);

  // The BYOK catalog is a server-side snapshot; this re-pulls it on demand.
  const refreshModels = useMutation({
    mutationFn: () => aiModelsApi.refresh(siteSlug),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['sites', siteSlug, 'ai-models'] });
      if (res.failed > 0) {
        toast.error(`Couldn't reach ${res.failed} provider(s) — kept the previous list`);
      } else {
        toast.success('Model list refreshed');
      }
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Refresh failed'),
  });

  // AB#669: free tool calls left on WordPuppi's key. The blanket invalidate at
  // the end of `run` refetches this along with everything else a turn touched.
  const usageQuery = useQuery({
    queryKey: ['sites', siteSlug, 'ai-usage'],
    queryFn: () => aiApi.usage(siteSlug),
  });
  const usage = usageQuery.data;
  const improvements = useQuery({
    queryKey: ['sites', siteSlug, 'ai-improvements'],
    queryFn: () => aiApi.improvements(siteSlug),
    enabled: improvementsOpen,
  });
  // Only ever needed to name the badge on a skill chat.
  const skillsQuery = useQuery({
    queryKey: ['ai-skills'],
    queryFn: () => aiApi.listSkills(),
    enabled: !!skill,
  });
  const skillName = skill
    ? (skillsQuery.data?.find((s) => s.id === skill)?.name ?? skill)
    : null;

  // Stable identity keeps the memoized ModelPicker out of every keystroke. AB#837.
  const chooseModel = useCallback((id: string) => {
    setModel(id);
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, id);
    } catch {
      // storage disabled — the choice just doesn't survive a reload
    }
  }, []);

  const context: AiChatContext = {
    route: location.pathname,
    ...(params.postId ? { post_id: params.postId } : {}),
    ...(params.pageId ? { page_id: params.pageId } : {}),
    ...(themeFile ? { theme_file: themeFile } : {}),
    ...(search.bp ? { bp: String(search.bp) } : {}),
    ...(skill ? { skill } : {}),
    ...(uploadedMedia.length ? { uploaded_media: uploadedMedia } : {}),
    ...(pinnedContext.length ? { pinned_context: pinnedContext } : {}),
  };

  const posts = useQuery({ queryKey: ['sites', siteSlug, 'baxter-context-posts'], queryFn: () => postsApi.list(siteSlug, { per_page: 50 }), enabled: contextMenuOpen });
  const pages = useQuery({ queryKey: ['sites', siteSlug, 'baxter-context-pages'], queryFn: () => pagesApi.list(siteSlug, { per_page: 50 }), enabled: contextMenuOpen });
  const media = useQuery({ queryKey: ['sites', siteSlug, 'baxter-context-media'], queryFn: () => mediaApi.list(siteSlug, { per_page: 50 }), enabled: contextMenuOpen });
  const theme = useQuery({ queryKey: ['sites', siteSlug, 'baxter-context-theme'], queryFn: () => themesApi.get(siteSlug), enabled: contextMenuOpen });
  const bundledThemes = useQuery({ queryKey: ['baxter-context-bundled-themes'], queryFn: () => sitesApi.listThemes(true), enabled: contextMenuOpen });
  const themeInfo = theme.data?.data;
  const themeFiles = themeInfo?.source === 'custom' ? themeInfo.files.map((file) => file.path)
    : bundledThemes.data?.data?.find((item) => item.id === themeInfo?.active_theme)?.files?.map((file) => file.path) ?? [];
  const contextCandidates: { group: string; pin: AiPinnedContext }[] = [
    ...(posts.data?.data ?? []).map((post) => ({ group: 'Posts', pin: { type: 'post' as const, id: post.id, title: post.title } })),
    ...(pages.data?.data ?? []).map((page) => ({ group: 'Pages', pin: { type: 'page' as const, id: page.id, title: page.title } })),
    ...themeFiles.map((path) => ({ group: 'Theme file', pin: { type: 'theme_file' as const, id: path, title: path } })),
    ...(media.data?.data ?? []).map((file) => ({ group: 'Media', pin: { type: 'media' as const, id: file.id, title: file.original_name } })),
    ...(selectionText && (params.pageId || params.postId) ? [{ group: 'Current selection', pin: { type: 'selection' as const, id: (params.pageId || params.postId)!, title: 'Current selection', excerpt: selectionText } }] :
      canvasSelection && canvasSelection.pageId === params.pageId ? [{ group: 'Current selection', pin: { type: 'selection' as const, id: canvasSelection.pageId, title: 'Current canvas selection', excerpt: canvasSelection.excerpt } }] : []),
  ];
  const mention = input.match(/@([^@\n]*)$/);
  const contextTerm = contextMenuOpen && mention ? mention[1].trim().toLowerCase() : '';
  const contextOptions = contextCandidates.filter(({ pin, group }) => !contextTerm || `${group} ${pin.title}`.toLowerCase().includes(contextTerm));
  const captureSelection = () => {
    const active = document.activeElement;
    if (active instanceof HTMLTextAreaElement && active !== composerRef.current && active.selectionStart !== active.selectionEnd) {
      setSelectionText(active.value.slice(active.selectionStart, active.selectionEnd).slice(0, 1_000));
      return;
    }
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    setSelectionText(node?.parentElement?.closest('[contenteditable="true"]') ? (selection?.toString() ?? '').slice(0, 1_000) : '');
  };
  const selectContext = (pin: AiPinnedContext) => {
    if (pinnedContext.length >= 8 && !pinnedContext.some((item) => item.type === pin.type && item.id === pin.id)) {
      toast.error('Remove a reference before adding another');
      return;
    }
    setPinnedContext((current) => current.some((item) => item.type === pin.type && item.id === pin.id)
      ? current.map((item) => item.type === pin.type && item.id === pin.id ? pin : item) : [...current, pin]);
    setInput((current) => current.replace(/@[^@\n]*$/, '').trimEnd());
    setContextMenuOpen(false);
    setContextMenuIndex(0);
    composerRef.current?.focus();
  };

  const chats = useQuery({
    queryKey: ['sites', siteSlug, 'ai-chats'],
    queryFn: () => aiApi.listChats(siteSlug),
    enabled: historyOpen,
  });

  // Keep the newest text in view while streaming.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  const stop = () => abortRef.current?.abort();
  useEffect(() => {
    if (!streaming || pending) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"], [data-slot="popover-content"], [role="listbox"]')) return;
      event.preventDefault();
      stop();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [streaming, pending]);

  // Leaving the panel mid-turn must not leave a stream running.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(
    () => () => {
      if (noteTimer.current) clearTimeout(noteTimer.current);
      objectUrls.current.forEach(URL.revokeObjectURL);
    },
    [],
  );

  // Switching to a model with no eyes drops the queued PICTURES — say why rather
  // than uploading pictures it can't look at. Other files stay (AB#949).
  useEffect(() => {
    if (canSee) return;
    const kept = attachments.filter((f) => !isImage(f));
    if (kept.length === attachments.length) return;
    setAttachments(kept);
    flashVisionNote();
  }, [canSee]);

  // The route unmounts the panel when it closes, but the chat is durable
  // server-side — reopen on whichever chat was last open. Mount only: a
  // [chatId] effect would race this restore and clear the key first. AB#645.
  // The New Site wizard hands the whole first turn over in localStorage; it
  // wins over the remembered chat (it IS the reason we're here). AB#642.
  useEffect(() => {
    const brief = readBaxterBrief(siteSlug);
    if (brief) {
      startBrief(brief);
      return;
    }
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(chatStorageKey(siteSlug));
    } catch {
      // storage disabled — nothing to restore
    }
    if (stored) void loadChat(stored, true);
  }, []);

  // AB#810: the canvas writes a brief while this panel is already open, so the
  // mount read above would never see it. Same channel, same `startBrief`.
  const queuedBrief = useRef<BaxterBrief | null>(null);
  useEffect(() => {
    const onBrief = (event: Event) => {
      if ((event as CustomEvent).detail !== siteSlug) return;
      const brief = readBaxterBrief(siteSlug);
      if (!brief) return;
      // Mid-turn `run` is a no-op, which would silently eat the ask.
      if (abortRef.current || pending) { queuedBrief.current = brief; return; }
      startBrief(brief);
    };
    window.addEventListener(BAXTER_BRIEF_EVENT, onBrief);
    return () => window.removeEventListener(BAXTER_BRIEF_EVENT, onBrief);
  }); // no dep array on purpose: the handler has to see the CURRENT turn state
  useEffect(() => {
    if (streaming || pending || !queuedBrief.current) return;
    const brief = queuedBrief.current;
    queuedBrief.current = null;
    startBrief(brief);
  }, [streaming, pending]);

  const patchLast = (fn: (m: Msg) => Msg) =>
    setMessages((prev) => prev.map((m, i) => (i === prev.length - 1 ? fn(m) : m)));

  const patchTool = (id: string, patch: Partial<AiToolCall>) =>
    setMessages((prev) =>
      prev.map((m) =>
        m.tool_calls?.some((t) => t.id === id)
          ? { ...m, tool_calls: m.tool_calls.map((t) => (t.id === id ? { ...t, ...patch } : t)) }
          : m,
      ),
    );

  /**
   * `ctxOverride` exists for the wizard brief only: its skill/media land in
   * state in the same tick, so `context` above is still the pre-brief one here.
   */
  async function run(
    request: AiChatRequest,
    optimistic?: Msg,
    ctxOverride?: Partial<AiChatContext>,
  ) {
    if (abortRef.current) return;
    const controller = new AbortController();
    abortRef.current = controller;
    lastRequestRef.current = request;
    setError(null);
    setPending(null);
    setServed(null);
    setStreaming(true);
    setStartedAt(Date.now());
    const turnId = startTurn();
    setMessages((prev) => [
      ...prev,
      ...(optimistic ? [optimistic] : []),
      { role: 'assistant', content: '' },
    ]);

    try {
      for await (const { event, data } of aiApi.streamChat(
        siteSlug,
        { ...request, context: { device_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...context, ...ctxOverride } },
        controller.signal,
      )) {
        if (controller.signal.aborted || abortRef.current !== controller) break;
        switch (event) {
          case 'chat':
            setChatId(data.id);
            rememberChat(siteSlug, data.id);
            if (data.skill) setSkill(data.skill); // AB#642
            // A first turn is sent with chat_id null; once the row exists, a
            // Retry of that turn must land in it, not open a second chat.
            if (lastRequestRef.current && !lastRequestRef.current.chat_id) {
              lastRequestRef.current = { ...lastRequestRef.current, chat_id: data.id };
            }
            break;
          case 'delta':
            patchLast((m) => ({ ...m, content: m.content + (data.text ?? '') }));
            break;
          case 'tool_start':
            toolCalls.current.set(data.id, { name: data.name, args: data.args });
            // An approved call already has its row (from approval_required) —
            // don't add a second one on the continuation stream.
            setMessages((prev) =>
              prev.some((m) => m.tool_calls?.some((t) => t.id === data.id))
                ? prev
                : prev.map((m, i) =>
                    i === prev.length - 1
                      ? {
                          ...m,
                          // Text after a tool call is a new segment; keep a paragraph break.
                          content: m.content && !m.content.endsWith('\n') ? `${m.content}\n\n` : m.content,
                          tool_calls: [
                            ...(m.tool_calls ?? []),
                            { id: data.id, name: data.name, args: data.args, status: 'pending' },
                          ],
                        }
                      : m,
                  ),
            );
            break;
          case 'tool_result':
            patchTool(data.id, {
              // The backend reports a user rejection as a failed result; keep it "Skipped".
              status: data.ok ? 'ok' : /^Rejected by the user/.test(data.summary ?? '') ? 'rejected' : 'error',
              summary: data.summary,
              screenshot_url: data.screenshot_url ?? null,
              preview_url: data.preview_url ?? null,
            });
            // The screen behind the panel is looking at the same data this call
            // just wrote: refetch exactly what it touched, and tell the route so
            // it can flash the rows that moved.
            {
              const call = toolCalls.current.get(data.id);
              // Args can be a whole page of HTML — don't hoard them for the chat's lifetime.
              toolCalls.current.delete(data.id);
              if (call && data.ok) {
                void applyToolEffects(queryClient, siteSlug, call, data).catch(() => {});
              }
            }
            break;
          case 'approval_required':
            toolCalls.current.set(data.id, { name: data.name, args: data.args });
            patchLast((m) => ({
              ...m,
              tool_calls: [
                ...(m.tool_calls ?? []),
                { id: data.id, name: data.name, args: data.args, status: 'pending' },
              ],
            }));
            setPending({ id: data.id, name: data.name, args: data.args, reason: data.reason });
            break;
          case 'free_exhausted':
            // NOT an error: the refused call already arrived as a failed
            // tool_result and the model keeps answering. Nudge once a mount.
            setFreeExhausted(true);
            void usageQuery.refetch();
            if (!nudgedRef.current) {
              nudgedRef.current = true;
              setSettingsOpen(true);
            }
            break;
          case 'error':
            setError(new Error(data.message || 'Baxter hit a problem.'));
            break;
          // The routed model, as soon as the provider's first chunk names it
          // (AB#669) — `done` repeats it, and never arrives at all when the
          // turn stops for an approval.
          case 'model':
          case 'done':
            if (data.model) setServed(data.model);
            break;
          default: // anything new the backend adds
            break;
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) setError(err); // desktop cancels reject as a plain Error
    } finally {
      setStreaming(false);
      setStartedAt(null);
      abortRef.current = null;
      endTurn(turnId);
      if (controller.signal.aborted) {
        toolCalls.current.clear();
        setMessages((previous) => previous.map((message) => ({ ...message,
          tool_calls: message.tool_calls?.map((call) => call.status === 'pending'
            ? { ...call, status: 'error', summary: 'Interrupted. An action already in progress may have completed.' } : call),
        })));
      }
      // Every tool call already invalidated what IT touched (see `tool_result`),
      // so a turn on a read-only tool now refetches nothing. What's left is what
      // no tool owns: the site row (last_built_at and friends) and the free-call
      // counter, both of which a turn can move without naming.
      // NOT `siteKeys.all` — `['sites']` is a prefix of every site-scoped key,
      // which would quietly be the blanket invalidate again.
      void queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteSlug) });
      void queryClient.invalidateQueries({ queryKey: siteKeys.settings(siteSlug) });
      void usageQuery.refetch();
    }
  }

  /** The muted "no eyes" line under the composer — no toast, it's a nudge. */
  const flashVisionNote = () => {
    setVisionNote(true);
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setVisionNote(false), 5000);
  };

  const addFiles = (picked: File[]) => {
    const { files, error } = acceptAttachments(attachments, picked, canSee);
    if (error) toast.error(error);
    setAttachments(files);
  };

  const send = () => {
    const text = input.trim();
    if (!text || !modelAvailable || streaming || pending || uploading) return;
    const files = attachments;
    setInput('');
    setContextMenuOpen(false);
    setAttachments([]);
    if (!files.length) {
      void run({ chat_id: chatId, message: text, model, skill }, { role: 'user', content: text, pinned_context: pinnedContext });
      return;
    }
    void sendWithAttachments(text, files);
  };

  /**
   * Files are uploaded first (like the wizard) and handed to the model as
   * `context.uploaded_media` for THIS turn only — nothing goes into state, so
   * the next turn is clean. A failed upload just doesn't ride along.
   */
  async function sendWithAttachments(text: string, files: File[]) {
    setUploading(true);
    const uploaded_media: NonNullable<AiChatContext['uploaded_media']> = [];
    const sent: File[] = [];
    for (const file of files) {
      try {
        const up = await mediaApi.upload(siteSlug, file, BAXTER_FOLDER as MediaFolder);
        if (up.data) {
          void notifyMediaChange(queryClient, siteSlug, [up.data], 'added').catch(() => {});
          uploaded_media.push({
            id: up.data.id,
            filename: up.data.filename,
            url: up.data.url,
            mime_type: up.data.mime_type,
          });
          sent.push(file);
        }
      } catch {
        toast.error(`Couldn't upload ${file.name} — sending without it`);
      }
    }
    setUploading(false);

    const message = withAttachmentNote(text, sent);
    const images = sent.filter(isImage).map((f) => URL.createObjectURL(f));
    objectUrls.current.push(...images);
    const others = sent
      .filter((f) => !isImage(f))
      .map((f) => ({ name: f.name, size: f.size, mime: attachmentMime(f) }));
    void run(
      { chat_id: chatId, message, model, skill },
      {
        role: 'user',
        content: message,
        pinned_context: pinnedContext,
        ...(images.length ? { images } : {}),
        ...(others.length ? { files: others } : {}),
      },
      uploaded_media.length ? { uploaded_media } : undefined,
    );
  }

  /**
   * AB#810: undo the page write this turn made, and nothing else. The exact
   * bytes the canvas reviewed go back with `expected_page` set to what the page
   * holds NOW, so a later edit by anyone loses the race instead of the person.
   */
  const revertPage = async (info: BaxterRevertPage) => {
    setReverting(info.page_id);
    try {
      const current = await pagesApi.get(siteSlug, info.page_id);
      const page = current.data;
      if (!page) throw new Error('That page no longer exists.');
      const data = {
        content: info.content ?? '',
        custom_css: info.custom_css ?? '',
        ...(info.editor_mode ? { editor_mode: info.editor_mode as PageEditorMode } : {}),
      };
      const result = await pagesApi.update(siteSlug, info.page_id, {
        ...data,
        expected_page: { content: page.content ?? null, custom_css: page.custom_css ?? null, editor_mode: page.editor_mode ?? null },
      });
      if (!result.success || !result.data) throw new Error(result.error?.message ?? 'The revert was not confirmed.');
      // Same signal a tool result raises: the canvas behind the panel reloads.
      await applyToolEffects(queryClient, siteSlug, { name: 'wpp_update_content', args: { site_slug: siteSlug, content_type: 'pages', id: info.page_id, data }, previousData: page }, { ok: true });
      toast.success('Reverted that change');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Revert failed');
    } finally {
      setReverting(null);
    }
  };

  const decide = (approved: boolean) => {
    if (!pending) return;
    if (!approved) patchTool(pending.id, { status: 'rejected' });
    void run({ chat_id: chatId, approval: { tool_call_id: pending.id, approved }, model });
  };

  const newChat = () => {
    abortRef.current?.abort();
    setMessages([]);
    setChatId(null);
    rememberChat(siteSlug, null);
    setPending(null);
    setError(null);
    setServed(null);
    setInput('');
    setPinnedContext([]);
    setContextMenuOpen(false);
    setSelectionText('');
    setSkill(null);
    setUploadedMedia([]);
    setAttachments([]);
    setModel(storedModel()); // a fresh chat starts from the user's saved default
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('sharedTemplate');
    if (!token) return;
    let active = true;
    void promptTemplatesApi.readShared(token).then((template) => {
      if (!active) return;
      url.searchParams.delete('sharedTemplate');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      newChat();
      setInput(template.prompt); // shared text is an editable user turn, never system instructions
      setSkill(template.skill ?? null);
    }).catch(() => { if (active) toast.error('That template share link is unavailable or revoked'); });
    return () => { active = false; };
  }, [siteSlug]);

  useEffect(() => {
    if (railAction?.siteSlug !== siteSlug) return;
    if (railAction?.kind === 'new') newChat();
    if (railAction?.kind === 'history') setHistoryOpen(true);
    // Each rail click sends a new object; a chat state update must not repeat it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railAction]);

  /**
   * The wizard's hand-off: a new chat whose first turn is already written.
   * Same `run` as the send button — no second streaming loop. AB#642.
   */
  const startBrief = (brief: BaxterBrief) => {
    // AB#810: a canvas ask is a turn of the chat that is ALREADY open — it keeps
    // the chat, the model and the skill; only the context grows a `selection`.
    if (brief.selection) {
      void run(
        { chat_id: chatId, message: brief.message, model, skill },
        { role: 'user', content: brief.message, selection: brief.selection, ...(brief.previous ? { revert: brief.previous } : {}) },
        { selection: brief.selection },
      );
      return;
    }
    const media = brief.media.map((m) => m.url);
    if (brief.model) chooseModel(brief.model);
    setSkill(brief.skill || null);
    setUploadedMedia(media);
    rememberChat(siteSlug, null);
    void run(
      {
        chat_id: null,
        message: brief.message,
        model: brief.model || model,
        skill: brief.skill || null,
      },
      { role: 'user', content: brief.message },
      { ...(brief.skill ? { skill: brief.skill } : {}), ...(media.length ? { uploaded_media: media } : {}), pinned_context: [] },
    );
  };

  /** `restore` = the remembered chat on mount; it may be gone, so fail quietly. */
  const loadChat = async (id: string, restore = false) => {
    abortRef.current?.abort();
    setHistoryOpen(false);
    try {
      const chat = await aiApi.getChat(siteSlug, id);
      setMessages(chat.messages as Msg[]);
      setPinnedContext(chat.pinned_context ?? []);
      setChatId(chat.id);
      rememberChat(siteSlug, chat.id);
      setPending(null);
      setError(null);
      setServed(null);
      setSkill(chat.skill ?? null);
      setUploadedMedia([]); // the wizard's uploads belong to that first turn only
      if (chat.model) setModel(chat.model); // restore this chat's own choice
    } catch (err) {
      if (restore) rememberChat(siteSlug, null); // deleted since — just start fresh
      else setError(err);
    }
  };

  const notConfigured = error instanceof ApiError && error.status === 503;
  const upgradeRequired = error instanceof ApiError && error.status === 402;
  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b px-3 py-2.5">
        <BaxterMark className="size-8" />
        <span className="font-semibold">Baxter</span>
        <Sparkles className="size-4 shrink-0 text-primary" aria-hidden="true" />
        {skillName && (
          // Which site-generator skill this chat runs (AB#642).
          <Badge variant="secondary" className="min-w-0 truncate text-[10px]" data-testid="baxter-skill-badge">
            ✨ {skillName}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="AI settings"
            title="AI settings"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings />
          </Button>
          {/* One slot, showing where Baxter would GO — not where it is. */}
          <Button
            variant="ghost"
            size="icon-sm"
            className="hidden md:inline-flex"
            aria-label={dock === 'right' ? 'Move Baxter to the bottom' : 'Move Baxter to the right'}
            title={dock === 'right' ? 'Move Baxter to the bottom' : 'Move Baxter to the right'}
            onClick={onToggleDock}
          >
            {dock === 'right' ? <PanelBottom /> : <PanelRight />}
          </Button>
          {/* AB#1065: a restored chat hides the empty-state Improve card; this reaches it in one click. */}
          <Button variant="ghost" size="icon-sm" aria-label="Improve this site" title="Improve this site" onClick={() => { newChat(); setImprovementsOpen(true); }}>
            <Wand2 />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label="New chat" title="New chat" onClick={newChat}>
            <Plus />
          </Button>
          <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
            <PopoverTrigger
              render={
                <Button variant="ghost" size="icon-sm" aria-label="Chat history" title="Chat history">
                  <Clock />
                </Button>
              }
            />
            <PopoverContent align="end" className="w-72 p-1">
              {chats.isLoading && <p className="p-2 text-sm text-muted-foreground">Loading…</p>}
              {chats.data?.length === 0 && (
                // Chats live in the per-site content DB, so this list is only
                // ever this site's — say so rather than implying none exist.
                <p className="p-2 text-sm text-muted-foreground">No chats yet for this site.</p>
              )}
              {chats.data?.map((c) => (
                <div key={c.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => void loadChat(c.id)}
                    className="flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    {c.title || 'Untitled chat'}
                  </button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete chat ${c.title || 'Untitled chat'}`}
                    onClick={async () => {
                      if (!(await confirmDialog(`Delete "${c.title || 'Untitled chat'}"? The transcript is gone for good; changes Baxter made to the site stay.`, { title: 'Delete chat?', destructive: true, confirmLabel: 'Delete chat' }))) return;
                      await aiApi.deleteChat(siteSlug, c.id);
                      if (c.id === chatId) newChat();
                      void chats.refetch();
                    }}
                  >
                    <Trash2 className="text-muted-foreground" />
                  </Button>
                </div>
              ))}
              {/* AB#834: one call clears every chat this account has on THIS site. */}
              {!!chats.data?.length && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 w-full justify-start border-t text-destructive"
                  onClick={async () => {
                    if (!(await confirmDialog('Delete every chat for this site? The transcripts are gone for good; changes Baxter made to the site stay.', { title: 'Clear all chats?', destructive: true, confirmLabel: 'Delete all chats' }))) return;
                    try {
                      await aiApi.clearChats(siteSlug);
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : 'Could not clear chats');
                      return;
                    }
                    newChat();
                    void chats.refetch();
                  }}
                >
                  <Trash2 /> Clear all chats
                </Button>
              )}
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon-sm" aria-label="Close Baxter" title="Close Baxter" onClick={onClose}>
            <X />
          </Button>
          {onCollapse && <Button variant="ghost" size="icon-sm" className="hidden md:inline-flex" aria-label="Collapse Baxter to icon rail" onClick={onCollapse}>
            <PanelRight />
          </Button>}
        </div>
      </header>

      <AiSettingsDialog
        siteSlug={siteSlug}
        open={settingsOpen}
        onOpenChange={(open) => {
          setSettingsOpen(open);
          // A key added in the dialog flips `byok` server-side; the counter
          // text would otherwise stay stale until the next turn.
          if (!open) void usageQuery.refetch();
        }}
        nudge={freeExhausted ? 'free-exhausted' : undefined}
      />

      <TemplateGallery
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        context={context}
        siteSlug={siteSlug}
        onUse={(template) => {
          setInput(template.prompt);
          // The server accepts a skill only on the first turn of a new chat.
          if (template.skill && !chatId) setSkill(template.skill);
        }}
      />

      {notConfigured ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          Baxter isn't configured on this server.
        </div>
      ) : (
        <>
          <div
            ref={listRef}
            data-processing={streaming ? 'true' : 'false'}
            aria-live="polite"
            className="flex-1 space-y-3 overflow-y-auto p-3 text-sm"
          >
            {isEmpty && (
              <div className="space-y-4 py-6">
                <div className="flex flex-col items-center gap-6 text-center">
                  <span className="baxter-pulse inline-flex">
                    <BaxterMark className="size-24" />
                  </span>
                  <p className="text-muted-foreground">
                    I'm Baxter, your AI co-editor. What are we working on?
                  </p>
                </div>
                <button type="button" onClick={() => setGalleryOpen(true)} className="group flex w-full items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left outline-none transition-colors hover:border-primary/60 hover:bg-accent/40 focus-visible:ring-[3px] focus-visible:ring-ring/50">
                  <Sparkles className="size-5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="flex-1"><span className="block font-medium">Start with a prompt template</span><span className="block text-xs text-muted-foreground">Browse ideas for writing, design and publishing.</span></span>
                  <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </button>
                <div className="rounded-xl border bg-card">
                  <button type="button" aria-expanded={improvementsOpen} onClick={() => setImprovementsOpen((open) => !open)} className="flex w-full items-center gap-3 px-4 py-3 text-left outline-none hover:bg-accent/40 focus-visible:ring-[3px] focus-visible:ring-ring/50">
                    <Sparkles className="size-5 shrink-0 text-primary" aria-hidden="true" />
                    <span className="flex-1 font-medium">Improve this site</span>
                    <ChevronRight className={cn('size-4 text-muted-foreground transition-transform ease-out', improvementsOpen && 'rotate-90')} aria-hidden="true" />
                  </button>
                  {improvementsOpen && <div className="disclosure-reveal space-y-1 border-t p-2">
                    {improvements.isPending && <p className="px-2 py-1 text-xs text-muted-foreground">Checking this site…</p>}
                    {improvements.isError && <p role="alert" className="px-2 py-1 text-xs text-destructive">Could not check this site. <button type="button" className="underline" onClick={() => void improvements.refetch()}>Retry</button></p>}
                    {improvements.isSuccess && improvements.data.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">Nothing urgent for this site.</p>}
                    {improvements.data?.map((suggestion) => <button key={`${suggestion.href}:${suggestion.title}`} type="button" disabled={streaming || !!pending || uploading || !modelAvailable} onClick={() => void run({ chat_id: chatId, message: suggestion.prompt, model, skill }, { role: 'user', content: suggestion.prompt })} className="block w-full rounded-md px-2 py-2 text-left outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50">
                      <span className="block text-sm font-medium">{suggestion.title}</span>
                      <span className="block text-xs text-muted-foreground">{suggestion.detail}</span>
                    </button>)}
                  </div>}
                </div>
              </div>
            )}

            {/* AB#943: once chatting, the same pulsing portrait heads the history
                and scrolls away with it rather than pinning in the header. */}
            {!isEmpty && (
              <div data-testid="baxter-history-portrait" className="flex justify-center py-6">
                <span className="baxter-pulse inline-flex">
                  <BaxterMark className="size-16" />
                </span>
              </div>
            )}

            {messages.map((m, i) =>
              m.role === 'user' ? (
                <UserMessage key={i} m={m} siteSlug={siteSlug} />
              ) : (
                <div key={i} className="space-y-1.5">
                  {m.content && <AssistantText content={m.content} />}
                  {m.tool_calls?.map((call) => <ToolRow key={call.id} call={call} siteSlug={siteSlug} />)}
                  {(() => {
                    // Only the turn that answered a canvas ask AND wrote that page.
                    const info = messages[i - 1]?.revert;
                    if (!info || !touchedPage(m.tool_calls, info.page_id)) return null;
                    return (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="baxter-revert"
                        disabled={reverting === info.page_id}
                        onClick={() => void revertPage(info)}
                      >
                        {reverting === info.page_id ? 'Reverting…' : 'Revert'}
                      </Button>
                    );
                  })()}
                  {streaming && !pending && startedAt !== null && i === messages.length - 1 && <Thinking startedAt={startedAt} />}
                </div>
              ),
            )}

            {pending && (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
                <p className="font-medium">{humanizeTool(pending.name)}</p>
                {pending.reason && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{pending.reason}</p>
                )}
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded border bg-background/60 p-2 text-[11px]">
                  {JSON.stringify(pending.args ?? {}, null, 2)}
                </pre>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => decide(true)}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decide(false)}>
                    Reject
                  </Button>
                </div>
              </div>
            )}

            {freeExhausted && (
              <div
                data-testid="baxter-free-banner"
                className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm"
              >
                {FREE_EXHAUSTED_COPY}{' '}
                <a
                  href={OPENROUTER_KEYS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  Get a free key
                </a>
              </div>
            )}

            {upgradeRequired && <UpgradeNotice error={error} />}

            {error != null && !upgradeRequired && (
              <div className="flex flex-wrap items-center gap-2 text-sm text-destructive">
                <span>{error instanceof Error ? error.message : 'Something went wrong.'}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => lastRequestRef.current && void run(lastRequestRef.current)}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>

          <div className="border-t p-3">
            {/* wrap: in the 24rem right dock the served-model readout would
                otherwise truncate to nothing between the picker and the
                free-actions counter. */}
            <div className="mb-3 flex flex-wrap items-center gap-y-2">
              <ModelPicker models={models} value={model} onChange={chooseModel} siteSlug={siteSlug} disabled={streaming || !!pending} />
              {hasPaidModels && (
                // Only BYOK catalogs go stale; nothing to re-pull on the free tier.
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="size-6 shrink-0 text-muted-foreground"
                  aria-label="Refresh model list"
                  title="Refresh model list"
                  disabled={refreshModels.isPending}
                  onClick={() => refreshModels.mutate()}
                >
                  <RefreshCw
                    className={cn('h-3.5 w-3.5', refreshModels.isPending && 'animate-spin')}
                    aria-hidden="true"
                  />
                </Button>
              )}
              {served && served !== model.slice(model.indexOf(':') + 1) && (
                <span
                  className="ml-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/70"
                  title={`This answer came from ${served}`}
                >
                  <span className="min-w-0 truncate">· {served}</span>
                  {servedModel && (
                    <ModelMeter
                      compact
                      strength={servedModel.strength}
                      speed={servedModel.speed}
                      vision={servedModel.vision}
                    />
                  )}
                </span>
              )}
              {usage && !usage.byok && model.startsWith('openrouter:') && (
                // Only when THIS pick would run on WordPuppi's key: an OpenRouter
                // model with no OpenRouter key of their own. A turn on the
                // person's OpenAI/Anthropic key never counts, so say nothing.
                // Quiet: the picker still works, this is only the running total
                // of free tool calls on WordPuppi's key. AB#669.
                <span
                  data-testid="baxter-free-left"
                  className="ml-1 shrink-0 text-xs text-muted-foreground/70"
                >
                  {usage.used >= usage.limit
                    ? '· no free actions left'
                    : `· ${usage.limit - usage.used} free actions left`}
                </span>
              )}
            </div>
            <Attachments
              files={attachments}
              onRemove={(i) => setAttachments((prev) => prev.filter((_, j) => j !== i))}
            />
            {pinnedContext.length > 0 && <div className="mb-2 flex flex-wrap gap-1" aria-label="Pinned context">
              {pinnedContext.map((pin) => <span key={`${pin.type}:${pin.id}`} className="inline-flex max-w-full items-center gap-1 rounded-full border bg-primary/10 px-2 py-1 text-xs text-foreground" data-testid="baxter-context-chip">
                <span className="truncate">{pin.title}</span>
                <button type="button" aria-label={`Remove ${pin.title}`} className="rounded-full p-0.5 hover:bg-muted" onClick={() => setPinnedContext((current) => current.filter((item) => item.type !== pin.type || item.id !== pin.id))}><X className="size-3" /></button>
              </span>)}
            </div>}
            {contextMenuOpen && <div id="baxter-context-menu" role="listbox" aria-label="Add site context" className="mb-2 max-h-56 overflow-y-auto rounded-lg border bg-popover p-1 shadow-lg">
              {contextOptions.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground">{posts.isLoading || pages.isLoading || media.isLoading || theme.isLoading ? 'Loading site items…' : 'No matching site items'}</p> : contextOptions.map(({ group, pin }, index) => <button key={`${pin.type}:${pin.id}`} type="button" role="option" aria-selected={index === contextMenuIndex} className={cn('flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm', index === contextMenuIndex ? 'bg-accent' : 'hover:bg-accent/60')} onMouseDown={(event) => event.preventDefault()} onClick={() => selectContext(pin)}>
                <span className="w-24 shrink-0 text-xs text-muted-foreground">{group}</span><span className="truncate">{pin.title}</span>
              </button>)}
            </div>}
            <div className="flex items-end gap-2">
              {/* AB#635: on desktop a bare file input crashes the webview. */}
              <input
                ref={attachInput}
                type="file"
                accept={ATTACH_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles([...(e.target.files ?? [])]);
                  e.target.value = '';
                }}
              />
              <Textarea
                ref={composerRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (!(e.nativeEvent as InputEvent).isComposing) {
                    const mentions = /(^|\s)@[^@\n]*$/.test(e.target.value);
                    setContextMenuOpen(mentions);
                    if (mentions) setSelectionText('');
                    setContextMenuIndex(0);
                  }
                }}
                disabled={!!pending}
                placeholder={pending ? 'Waiting for your approval above' : 'Ask Baxter…'}
                aria-label="Message Baxter"
                aria-controls={contextMenuOpen ? 'baxter-context-menu' : undefined}
                aria-expanded={contextMenuOpen}
                rows={2}
                className="max-h-40 min-h-[2.75rem] resize-none"
                onPaste={(e) => {
                  // Text pastes fall straight through — only a picture is ours.
                  const images = imagesFromClipboard(e.clipboardData);
                  if (!images.length) return;
                  e.preventDefault();
                  if (canSee) addFiles(images);
                  else flashVisionNote();
                }}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;
                  if (contextMenuOpen) {
                    if (e.key === 'Escape') { e.preventDefault(); setContextMenuOpen(false); return; }
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      setContextMenuIndex((index) => (index + (e.key === 'ArrowDown' ? 1 : -1) + contextOptions.length) % Math.max(contextOptions.length, 1));
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (contextOptions.length) selectContext(contextOptions[Math.min(contextMenuIndex, contextOptions.length - 1)].pin);
                      return;
                    }
                  }
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <div className="flex shrink-0 translate-y-1 flex-col items-center gap-1">
                <Button size="icon" variant="ghost" className="rounded-full" aria-label="Add site context" disabled={!!pending || streaming} onMouseDown={captureSelection} onClick={() => { setContextMenuOpen((open) => !open); setContextMenuIndex(0); composerRef.current?.focus(); }}><AtSign /></Button>
                {/* A disabled button fires no mouse events, so the tooltip lives on the wrapper. */}
                <span
                  title={
                    canSee
                      ? 'Attach a file'
                      : "Attach a file — this model can't see pictures; pick one with the eye icon to send pictures"
                  }
                >
                  <Button
                    size="icon"
                    variant="ghost"
                    className="rounded-full"
                    aria-label="Attach a file"
                    data-testid="baxter-attach"
                    disabled={!!pending || streaming || uploading}
                    onClick={(e) => {
                      if (!pickFilesDesktop(e, { multiple: true, accept: ATTACH_ACCEPT }, addFiles))
                        attachInput.current?.click();
                    }}
                  >
                    <Paperclip />
                  </Button>
                </span>
                {streaming ? (
                  <Button
                    size="icon"
                    variant="secondary"
                    className="rounded-full"
                    aria-label="Stop generating"
                    onClick={stop}
                  >
                    <Square />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    className="baxter-send rounded-full"
                    aria-label="Send message"
                    disabled={!input.trim() || !modelAvailable || !!pending || uploading}
                    onClick={send}
                  >
                    <ArrowUp data-anim="send" />
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              {visionNote ? (
                <span data-testid="baxter-no-vision">{NO_VISION_NOTE}</span>
              ) : uploading ? (
                'Uploading…'
              ) : pending ? (
                'Waiting for your approval above'
              ) : (
                'Baxter can make mistakes. Check what it changed.'
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
